/**
 * StreamRiftGate - Password gate for the StreamRift view.
 *
 * Simple SHA-256 hash check. SessionStorage persistence (tab-scoped).
 * Reuses the same Web Crypto pattern as GateView.tsx.
 */

import { useState, useCallback } from 'react';

const STREAMRIFT_HASH = 'a9669b23c4091e2c098883504e59a288ac7557a5d3e52f52677670a0f13f5e1a';

async function sha256(message: string): Promise<string> {
  const data = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface StreamRiftGateProps {
  onUnlock: () => void;
}

export function StreamRiftGate({ onUnlock }: StreamRiftGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password.trim()) return;

      setVerifying(true);
      setError(null);

      try {
        const hash = await sha256(password);
        if (hash === STREAMRIFT_HASH) {
          sessionStorage.setItem('streamrift_unlocked', 'true');
          onUnlock();
        } else {
          setError('Incorrect password');
          setPassword('');
        }
      } catch {
        setError('Verification failed');
      } finally {
        setVerifying(false);
      }
    },
    [password, onUnlock]
  );

  return (
    <div className="flex h-full items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-foreground text-xl font-bold">StreamRift</h1>
          <p className="text-muted-foreground mt-1 text-sm">Enter password to continue</p>
        </div>

        <div className="bg-card border-border rounded-lg border p-6">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoFocus
                disabled={verifying}
                className="bg-background border-border text-foreground w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={verifying || !password.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 mt-3 w-full rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              {verifying ? 'Verifying...' : 'Enter'}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-destructive/10 border-destructive/20 mt-3 rounded border p-2">
            <p className="text-destructive text-center text-xs">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
